import React, { useContext, useEffect, useState, useRef } from 'react';
import { firestore } from '../Firebase';
import { useParams } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { AuthContextPublic } from '../context/AuthContextPublic';

const Chat = ({ features = {} }) => {
  const { userName } = useContext(AuthContextPublic) || {};

  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const { videoID: paramVideoID } = useParams();
  const messagesEndRef = useRef(null);
  const [chatVisible, setChatVisible] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    const unsubscribeMessages = firestore
      .collection('videos')
      .doc(paramVideoID)
      .collection('messages')
      .orderBy('timestamp', 'asc')
      .onSnapshot((querySnapshot) => {
        setChatMessages(
          querySnapshot.docs
            .map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }))
            .filter((message) => message.visible === 1)
        );
      });

    const unsubscribeChatVisible = firestore
      .collection('videos')
      .doc(paramVideoID)
      .onSnapshot((doc) => {
        setChatVisible(doc.data()?.chatVisible === 1);
      });

    // Clean up both listeners when component unmounts
    return () => {
      unsubscribeMessages();
      unsubscribeChatVisible();
    };
  }, [paramVideoID]);

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;

    try {
      await firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('messages')
        .add({
          text: messageInput,
          name: userName || 'Anonymous',
          timestamp: serverTimestamp(),
          visible: 1,
        });

      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="chat-container" style={styles.container}>
      <h5 className={`ps-2 ${features.hasDarkModeOn ? 'text-white' : ''}`}>
        Live Chat
      </h5>
      {chatVisible ? (
        <>
          <div
            className="messages-window"
            style={{
              ...styles.messagesWindow,
              backgroundColor: features.hasDarkModeOn ? '#343a40' : '#f5f5f5',
            }}
          >
            {chatMessages.map((message, index) => (
              <div
                key={index}
                style={{
                  ...styles.message,
                  backgroundColor: features.hasDarkModeOn ? '#495057' : '#fff',
                }}
              >
                <div style={styles.messageContent}>
                  <span
                    style={{
                      ...styles.messageName,
                      color: features.hasDarkModeOn ? '#e9ecef' : 'inherit',
                    }}
                  >
                    {message.name}:
                  </span>
                  <span
                    style={{
                      ...styles.messageText,
                      color: features.hasDarkModeOn ? '#e9ecef' : 'inherit',
                    }}
                  >
                    {message.text}
                  </span>
                </div>
                <div
                  style={{
                    ...styles.messageTimestamp,
                    color: features.hasDarkModeOn ? '#adb5bd' : '#666',
                  }}
                >
                  {message.timestamp?.toDate().toLocaleTimeString()}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </>
      ) : (
        <div
          style={{
            ...styles.chatDisabled,
            backgroundColor: features.hasDarkModeOn ? '#343a40' : '#f5f5f5',
            color: features.hasDarkModeOn ? '#adb5bd' : '#666',
          }}
        >
          Chat is currently disabled
        </div>
      )}
      <div
        className="input-area"
        style={{
          ...styles.inputArea,
          backgroundColor: features.hasDarkModeOn ? '#212529' : '#fff',
        }}
      >
        <div style={styles.inputWrapper}>
          <textarea
            placeholder="Type a message..."
            style={{
              ...styles.input,
              backgroundColor: features.hasDarkModeOn ? '#343a40' : '#fff',
              color: features.hasDarkModeOn ? '#e9ecef' : 'inherit',
              borderColor: features.hasDarkModeOn ? '#495057' : '#ccc',
            }}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <button
            style={{
              ...styles.button,
              backgroundColor: features.hasDarkModeOn ? '#0d6efd' : '#0d6efd',
            }}
            onClick={handleSendMessage}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '500px',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  messagesWindow: {
    flex: 1,
    padding: '5px',
    overflowY: 'auto',
    backgroundColor: '#f5f5f5',
  },
  inputArea: {
    padding: '4px 5px 1px 5px',
    backgroundColor: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  input: {
    flex: 1,
    padding: '3px 6px',
    borderRadius: '4px 0 0 4px',
    border: '1px solid #ccc',
    borderRight: 'none',
    resize: 'vertical',
    minHeight: '60px',
    maxHeight: '150px',
    boxSizing: 'border-box',
    width: '0',
    fontSize: '13px',
  },
  button: {
    padding: '8px',
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '0 4px 4px 0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '13px',
  },
  message: {
    marginBottom: '4px',
    padding: '6px 10px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  messageContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  messageText: {
    fontSize: '13px',
  },
  messageTimestamp: {
    fontSize: '11px',
    color: '#666',
    marginTop: '2px',
  },
  messageName: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  chatDisabled: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: '#f5f5f5',
    color: '#666',
    fontSize: '14px',
  },
};

export default Chat;
